import { WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import { useModalState } from "@clipboard-health/ui-components";
import { isDefined, isNil } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { type ClipboardScoreLog } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreHistory";
import { type ClipboardScoreProfileResponse } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { type Worker } from "@src/appV2/Worker/api/types";
import { compareAsc, parseISO } from "date-fns";
import { isEmpty } from "lodash";
import { useEffect } from "react";

import { useEmergencyCreditsEducation } from "../../hooks/useEmergencyCreditsEducation";
import { isScoreHealthy } from "../../utils/utils";
import { RestrictionsBottomSheet } from "../BottomSheets/RestrictionsBottomSheet";
import { EmergencyCreditsEducationContainer } from "../EmergencyCreditsEducation/EmergencyCreditsEducationContainer";
import { EmergencyCreditsInfo } from "./EmergencyCreditsInfo";
import { HelpAndSupport } from "./HelpAndSupport";
import { History } from "./History";
import { Info } from "./Info";
import { ClipboardScoreHeader } from "./ScoreHeader";

export interface ClipboardScorePageContentProps {
  worker: Worker;
  workerQualification: string;
  clipboardScoreProfile: ClipboardScoreProfileResponse;
  logs: ClipboardScoreLog[];
  isDynamicPenaltiesEnabled: boolean;
  accessMetadata?: {
    basketSize: number;
    scoreThreshold: number;
  };
  isFinalRestrictionNext: boolean;
  nextRestrictionDurationInDays: number;
  restrictionCount: number;
  finalRestrictionEvalWindow: number;
}

function getLastShiftLogs(logs: ClipboardScoreLog[]): ClipboardScoreLog[] {
  if (logs.length === 0) {
    return logs;
  }

  const logsSortedByDate = logs.sort((a, b) =>
    compareAsc(parseISO(b.createdAt), parseISO(a.createdAt))
  );

  const lastLog = logsSortedByDate.at(0);

  if (isNil(lastLog)) {
    return [];
  }

  return isEmpty(lastLog.shiftId)
    ? [lastLog]
    : logsSortedByDate.filter((log) => log.shiftId === lastLog.shiftId);
}

export function ClipboardScorePageContent(props: ClipboardScorePageContentProps) {
  const {
    worker,
    workerQualification,
    clipboardScoreProfile,
    logs,
    isDynamicPenaltiesEnabled,
    accessMetadata,
    isFinalRestrictionNext,
    nextRestrictionDurationInDays,
    restrictionCount,
    finalRestrictionEvalWindow,
  } = props;
  const { tmz } = worker;
  const {
    clipboardScore,
    clipboardScoreRank,
    accountStatus,
    restriction,
    clipboardScorePenaltyMultipliers: multipliers,
    emergencyCreditStats,
  } = clipboardScoreProfile;

  const restrictionsBottomSheet = useModalState();
  const emergencyCreditsEducation = useModalState();
  const {
    hasSeenEmergencyCreditsEducation,
    shouldShowEmergencyCreditsEducation,
    markEmergencyCreditsEducationAsSeen,
    isEmergencyCreditsEnabled,
    emergencyCreditsConfig,
  } = useEmergencyCreditsEducation();

  useEffect(() => {
    if (shouldShowEmergencyCreditsEducation) {
      emergencyCreditsEducation.openModal();
    }
  }, [shouldShowEmergencyCreditsEducation, emergencyCreditsEducation]);

  const isWorkerRestricted: boolean = accountStatus?.status === WorkerStage.RESTRICTED;

  const lastShiftLogs = getLastShiftLogs(logs);

  const logsWithShiftId = logs.filter((log) => !isEmpty(log.shiftId));

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          paddingBottom: 4,
          overflowY: "auto",
          overflowX: "hidden",
          flexGrow: 1,
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        <Box
          sx={{
            background: (theme) =>
              isScoreHealthy(clipboardScore)
                ? theme.palette.onboarding?.inProgress.gradient
                : theme.palette.onboarding?.error.gradient,
          }}
        >
          <ClipboardScoreHeader
            score={clipboardScore}
            ranking={clipboardScoreRank}
            qualification={workerQualification}
            accountStatus={accountStatus}
            reEnrollmentScore={restriction?.reEnrollmentScore ?? 80}
            tmz={tmz}
          />
        </Box>
        <Box
          sx={{
            paddingX: 5,
          }}
        >
          <Stack spacing={6} paddingTop={8}>
            {/** if FF is off, this prop won't be here - this is the pseudo FF check */}
            {isDefined(emergencyCreditStats) && !isWorkerRestricted && (
              <EmergencyCreditsInfo
                emergencyCreditStats={emergencyCreditStats}
                onLearnMoreClick={emergencyCreditsEducation.openModal}
              />
            )}
            <Info
              score={clipboardScore}
              isWorkerRestricted={isWorkerRestricted}
              areDynamicPenaltiesEnabled={isDynamicPenaltiesEnabled}
              isFinalRestrictionNext={isFinalRestrictionNext}
              nextRestrictionDurationInDays={nextRestrictionDurationInDays}
              restrictionCount={restrictionCount}
              finalRestrictionEvalWindow={finalRestrictionEvalWindow}
              accessMetadata={accessMetadata}
              multipliers={multipliers}
              hasWorkedShifts={logsWithShiftId.length > 0}
              restrictionsBottomSheet={restrictionsBottomSheet}
            />
            <History
              logs={lastShiftLogs}
              isDynamicPenaltiesEnabled={isDynamicPenaltiesEnabled}
              policy={clipboardScoreProfile.policy}
              isEmergencyCreditsEnabled={isEmergencyCreditsEnabled}
            />
            <HelpAndSupport />
          </Stack>
        </Box>
      </Box>
      <RestrictionsBottomSheet
        modalState={restrictionsBottomSheet}
        nextRestrictionDurationInDays={nextRestrictionDurationInDays}
      />
      {isDefined(emergencyCreditsConfig) && (
        <EmergencyCreditsEducationContainer
          modalState={emergencyCreditsEducation}
          markAsSeen={markEmergencyCreditsEducationAsSeen}
          showCloseButton={hasSeenEmergencyCreditsEducation}
          emergencyCreditsConfig={emergencyCreditsConfig}
        />
      )}
    </>
  );
}
